import { history } from 'umi';
import { makeObservable, action, observable, runInAction } from 'mobx';
import { BaseService } from '../base';
import Home from './api.interface';
import { HomeModel } from './home.model';
import { DisburseSummaryModel } from './disburseSummary.model';
import { UC_OTP_SCENE, UC_TOGGLE_STATUS } from '../userInfo/userInfo.interface';

/**
 * 首页信息接口(query_homepage_info_new)的加载选项。
 * 页面首次加载传 redirectOnError：拿不到数据就进异常页，否则默认值会把额度渲染成 0、开关渲染成已关闭，比异常页更误导；
 * 操作后的刷新不传：接口失败只由 request 层 toast，页面保留旧数据，不把已经操作成功的用户踢出去。
 * 放款摘要(query_disburse_summary)不走这个规则，失败只 toast、金额显示 0。
 */
export interface IHomeLoadOptions {
  redirectOnError?: boolean;
}

export class CommonService extends BaseService {
  @observable home: HomeModel = new HomeModel();

  /** 首页放款摘要(独立 model)，数据来源 query_disburse_summary */
  @observable disburseSummary: DisburseSummaryModel = new DisburseSummaryModel();

  constructor() {
    super();
    makeObservable(this);
  }

  @action
  async getFEProductConfigs() {
    const res = await this.post(Home.RefactorAPI.get_fe_product_config);
    return res;
  }

  @action
  async getHomeInfo(options: IHomeLoadOptions = {}): Promise<HomeModel | null> {
    const { code, result } = await this.post(Home.RefactorAPI.get_homepage_info_new);
    if (code !== 0 && this.redirectIfFirstLoadFailed(options)) {
      return null;
    }
    if (code === 0) {
      runInAction(() => {
        this.home = {
          ...this.home,
          ...result,
        };
      });
    }
    return result || this.home;
  }

  /**
   * 首页信息接口失败时的统一处理，见 IHomeLoadOptions：
   * 首次加载进异常页并返回 true；刷新场景返回 false，由调用方保留旧数据
   */
  protected redirectIfFirstLoadFailed(options: IHomeLoadOptions) {
    if (!options.redirectOnError) {
      return false;
    }
    history.replace('/exception');
    return true;
  }

  /**
   * 首页放款摘要：到账金额与放款时间。
   * 与 getHomeInfo 拆开是因为这边要扫候选单做试算，比首页信息重。
   */
  @action
  async getDisburseSummary() {
    const { code, result } = await this.post(Home.RefactorAPI.query_disburse_summary);
    if (code !== 0) {
      // 摘要失败不进异常页：首页其它部分照常，到账金额显示 0，request 层已 toast
      return false;
    }
    runInAction(() => {
      this.disburseSummary = {
        loan1_disburse_amount: result?.loan1_disburse_amount ?? '0',
        loan2_disburse_amount: result?.loan2_disburse_amount ?? '0',
        to_be_received_amount: result?.to_be_received_amount ?? '0',
        loan1_disburse_time: result?.loan1_disburse_time ?? 0,
        loan2_disburse_time: result?.loan2_disburse_time ?? 0,
      } as DisburseSummaryModel;
    });
    return true;
  }

  @action
  async updateUserConfig(params: {
    toggle_status?: UC_TOGGLE_STATUS;
    disburse_percentage?: number;
    fee_change_type?: number;
  }) {
    return await this.post(Home.API.update_user_config, {
      body: params,
    });
  }

  @action
  async updateFirstActivate() {
    return await this.post(Home.API.update_first_activate);
  }

  @action
  async getLatestDisbursedGroups() {
    return await this.post(Home.API.get_user_latest_disbursed_groups);
  }

  @action
  async getUserMobile(scene: UC_OTP_SCENE) {
    return await this.post(Home.API.get_user_mobile_number, {
      body: { otp_scene: scene },
    });
  }

  /**
   *
   * 单独更新设置的比例，也可以请求方式getHomeInfo来更新
   * @param {number} [percentage=0]
   * @memberof HomeService
   */
  @action
  async updatePercentage(percentage: number = 0) {
    this.home = {
      ...this.home,
      user_percentage: percentage,
    };
  }
}
